import { Badge, Banner, Button, EmptyState, Select, Spinner, TextArea, TextField } from '@ui';
import { PlusIcon, SearchIcon, TrashIcon } from '../icons';
import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { EPIC_CLOSING_STATUS, EPIC_RESOLUTION_TITLES, EPIC_RESOLUTIONS, EPIC_STATUS_TITLES, EPIC_STATUSES, epicMoveAllowed, type EpicResolution } from '../../../shared/board';
import { BUG_STATUS_LABELS, bugApi, type Epic, type EpicDetail, type EpicLinkKind } from '../bugApi';

// Epics: the backlog list (Эб1: «To Do» epics, take one into work, create) and the card of an epic (Э1–Э12, model quint/specs/epics.qnt;
// stage and resolution — Ап1–Ап2, epic_process.qnt). The board of «В работе и готово» is EpicKanban.tsx (Ап5).

type OnError = (err: unknown, fallback: string) => void;

const errorText = (err: unknown, fallback: string) => (err instanceof Error && err.message ? err.message : fallback);
const KIND_OPTIONS = [
  { value: 'child', label: 'входит в эпик' },
  { value: 'rel', label: 'связан с' },
];
const STATUS_OPTIONS = EPIC_STATUSES.map((value) => ({ value, label: EPIC_STATUS_TITLES[value] }));
const RESOLUTION_OPTIONS = EPIC_RESOLUTIONS.map((value) => ({ value, label: EPIC_RESOLUTION_TITLES[value] }));

/** Эб1, Эб2, Ап5: `backlog` — epics in To Do (take them into work); `started` — every epic (the board has a To Do column too). */
export type EpicPart = 'backlog' | 'started';

export const inPart = (epic: Pick<Epic, 'status'>, part: EpicPart): boolean => part === 'started' || epic.status === 'todo';

export default function EpicList({ onError, part = 'started' }: { onError: OnError; part?: EpicPart }) {
  const [epics, setEpics] = useState<Epic[] | null>(null);
  const [text, setText] = useState('');
  const [creating, setCreating] = useState(false);
  const [openId, setOpenId] = useState<number | null>(null);

  const load = useCallback(async () => {
    try {
      setEpics((await bugApi.epics()).epics);
    } catch (err) {
      onError(err, 'Не удалось загрузить эпики');
    }
  }, [onError]);

  useEffect(() => {
    void load();
  }, [load]);

  const put = useCallback((epic: Epic) => setEpics((prev) => (prev ?? []).map((e) => (e.id === epic.id ? epic : e))), []);

  const shown = useMemo(() => {
    const q = text.trim().toLowerCase();
    return (epics ?? []).filter((e) => inPart(e, part) && (!q || [`№${e.id}`, e.title, e.description].some((v) => v.toLowerCase().includes(q))));
  }, [epics, text, part]);

  const take = async (epic: Epic) => {
    try {
      put((await bugApi.updateEpic(epic.id, { status: 'discovery', expectedStatus: epic.status })).epic);
    } catch (err) {
      onError(err, 'Не удалось взять эпик в работу');
    }
  };

  if (!epics) return <Spinner label="Загрузка эпиков" />;
  const row = (epic: Epic) => (
    <div key={epic.id} className={`epic-row${openId === epic.id ? ' is-open' : ''}`}>
      <span className="mono" data-bug-open="">
        ◆ {epic.id}
      </span>
      <button type="button" className="epic-backlog-title" data-pii="" onClick={() => setOpenId(openId === epic.id ? null : epic.id)}>
        {epic.title}
      </button>
      <Progress done={epic.progress.done} total={epic.progress.total} />
      {part === 'backlog' ? (
        <Button size="sm" variant="tonal" onClick={() => void take(epic)}>
          Взять в работу
        </Button>
      ) : (
        <span className="board-card-meta" data-bug-open="">
          {EPIC_STATUS_TITLES[epic.status as keyof typeof EPIC_STATUS_TITLES]}
          {epic.resolution ? ` · ${EPIC_RESOLUTION_TITLES[epic.resolution as EpicResolution] ?? epic.resolution}` : ''}
        </span>
      )}
    </div>
  );
  const detail = (epic: Epic) =>
    openId === epic.id && (
      <EpicCard
        key={`card-${epic.id}`}
        id={epic.id}
        onChanged={put}
        onDeleted={() => {
          setEpics((prev) => (prev ?? []).filter((e) => e.id !== epic.id));
          setOpenId(null);
        }}
        onError={onError}
      />
    );
  return (
    <div className="board">
      <div className="board-toolbar">
        <TextField
          label="Поиск по эпикам"
          hideLabel
          size="sm"
          type="search"
          icon={<SearchIcon />}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Номер, название"
          className="board-search"
        />
        {part === 'backlog' && (
          <Button size="sm" variant="primary" icon={<PlusIcon />} aria-expanded={creating} onClick={() => setCreating(!creating)}>
            Эпик
          </Button>
        )}
      </div>
      {creating && (
        <NewEpic
          onCreated={(epic) => {
            setEpics((prev) => [epic, ...(prev ?? [])]);
            setCreating(false);
            setOpenId(epic.id);
          }}
        />
      )}
      {shown.length === 0 && <EmptyState compact>{part === 'backlog' ? 'В бэклоге эпиков пусто' : 'Эпиков нет'}</EmptyState>}
      <div className="epic-list">
        {shown.map((e) => [row(e), detail(e)])}
      </div>
    </div>
  );
}

/** Э8: «закрыто N из M» of the «входит» reports. */
function Progress({ done, total }: { done: number; total: number }) {
  if (!total)
    return (
      <span className="board-card-meta" data-bug-open="">
        нет репортов
      </span>
    );
  return (
    <span className="epic-progress" title={`Закрыто ${done} из ${total}`}>
      <span className="epic-progress-bar">
        <span style={{ width: `${Math.round((done / total) * 100)}%` }} />
      </span>
      <span className="board-card-meta" data-bug-open="">
        {done} / {total}
      </span>
    </span>
  );
}

function NewEpic({ onCreated }: { onCreated: (epic: Epic) => void }) {
  const [title, setTitle] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      onCreated((await bugApi.createEpic({ title: title.trim() })).epic);
    } catch (err) {
      setError(errorText(err, 'Не удалось создать эпик'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="board-editor" onSubmit={submit} aria-label="Новый эпик">
      <div className="board-actions">
        <TextField label="Название эпика" size="sm" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} className="board-search" />
        <Button type="submit" size="sm" variant="primary" busy={busy} disabled={busy || !title.trim()}>
          ✓ Создать
        </Button>
      </div>
      {error && <Banner tone="error">{error}</Banner>}
    </form>
  );
}

/** The epic: title, description; reports by link kind (Э6–Э7), link by number, unlink. */
export function EpicCard({
  id,
  onChanged,
  onDeleted,
  onError,
}: {
  id: number;
  onChanged: (epic: Epic) => void;
  onDeleted: () => void;
  onError: OnError;
}) {
  const [detail, setDetail] = useState<EpicDetail | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [reportId, setReportId] = useState('');
  const [kind, setKind] = useState<EpicLinkKind>('child');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [sure, setSure] = useState(false);
  const [closing, setClosing] = useState(false);

  const take = useCallback(
    (next: EpicDetail) => {
      setDetail(next);
      setClosing(false);
      setTitle(next.epic.title);
      setDescription(next.epic.description);
      onChanged(next.epic);
    },
    [onChanged],
  );

  useEffect(() => {
    bugApi
      .epic(id)
      .then(take)
      .catch((err) => onError(err, 'Не удалось открыть эпик'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const run = async (action: () => Promise<EpicDetail | void>, fallback: string) => {
    setBusy(true);
    setError('');
    try {
      const next = await action();
      if (next) take(next);
    } catch (err) {
      setError(errorText(err, fallback));
    } finally {
      setBusy(false);
    }
  };

  if (!detail) return <Spinner label="Загрузка эпика" />;
  const { epic, links } = detail;
  const changed = title.trim() !== epic.title || description.trim() !== epic.description;
  const group = (k: EpicLinkKind) => links.filter((l) => l.kind === k);

  return (
    <div className="board-editor epic-detail" aria-label={`Эпик №${epic.id}`}>
      <div className="board-actions">
        <TextField label="Название" size="sm" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} className="board-search" />
        <Select
          label="Этап"
          size="sm"
          value={epic.status}
          disabled={busy}
          onChange={(value) => {
            if (value === epic.status || !epicMoveAllowed(epic.status, value)) return;
            // Ап2: Delivery Done asks for the resolution first.
            if (value === EPIC_CLOSING_STATUS) setClosing(true);
            else void run(() => bugApi.updateEpic(epic.id, { status: value, expectedStatus: epic.status }), 'Не удалось сменить этап');
          }}
          options={STATUS_OPTIONS}
        />
        {(closing || epic.status === EPIC_CLOSING_STATUS) && (
          <Select
            label="Резолюция"
            size="sm"
            value={epic.resolution ?? ''}
            placeholder="Резолюция…"
            disabled={busy}
            onChange={(value) => {
              setClosing(false);
              void run(() => bugApi.updateEpic(epic.id, { status: EPIC_CLOSING_STATUS, resolution: value, expectedStatus: epic.status }), 'Не удалось сменить резолюцию');
            }}
            options={RESOLUTION_OPTIONS}
          />
        )}
      </div>
      <TextArea label="Описание" size="sm" value={description} onChange={(e) => setDescription(e.target.value)} maxLength={5000} />
      <div className="board-actions">
        <Button
          size="sm"
          variant="primary"
          disabled={busy || !changed || !title.trim()}
          onClick={() => void run(() => bugApi.updateEpic(epic.id, { title: title.trim(), description: description.trim() }), 'Не удалось сохранить')}
        >
          ✓ Сохранить
        </Button>
        {sure ? (
          <Button size="sm" variant="danger" disabled={busy} onClick={() => void run(async () => (await bugApi.removeEpic(epic.id), onDeleted()), 'Не удалось удалить')}>
            Точно удалить? Репорты останутся
          </Button>
        ) : (
          <Button size="sm" variant="quiet" icon={<TrashIcon />} disabled={busy} onClick={() => setSure(true)}>
            Удалить
          </Button>
        )}
      </div>
      {(['child', 'rel'] as const).map((k) => (
        <div key={k} className="epic-links">
          <div className="board-lane-title">
            {k === 'child' ? `Даунстрим: входят · ${epic.progress.done} / ${epic.progress.total}` : `Связаны · ${group(k).length}`}
          </div>
          {group(k).length === 0 ? (
            <span className="board-card-meta">—</span>
          ) : (
            group(k).map((l) => (
              <div key={`${l.reportId}-${k}`} className="epic-link">
                <span className="mono" data-bug-open="">
                  №{l.reportNumber ?? l.reportId}
                </span>
                <Badge tone={l.status === 'fixed' ? 'success' : l.status === 'backlog' ? 'warning' : 'neutral'}>{BUG_STATUS_LABELS[l.status]}</Badge>
                <span className="board-card-text" data-pii="">
                  {l.description || '(без описания)'}
                </span>
                <span className="board-card-meta" data-pii="">
                  {l.project}
                </span>
                <Button size="sm" variant="quiet" aria-label={`Снять связь с ${l.project} №${l.reportNumber ?? l.reportId}`} disabled={busy} onClick={() => void run(() => bugApi.unlinkReport(epic.id, l.reportId, k), 'Не удалось снять связь')}>
                  ✕
                </Button>
              </div>
            ))
          )}
        </div>
      ))}
      <form
        className="board-actions"
        onSubmit={(e) => {
          e.preventDefault();
          void run(async () => {
            const next = await bugApi.linkReportNumber(epic.id, Number(reportId), kind);
            setReportId('');
            return next;
          }, 'Не удалось связать');
        }}
      >
        <TextField
          label="Репорт №"
          size="sm"
          inputMode="numeric"
          value={reportId}
          onChange={(e) => setReportId(e.target.value.replace(/\D/g, ''))}
          className="board-dup-number"
        />
        <Select label="Связь" size="sm" value={kind} onChange={(v) => setKind(v as EpicLinkKind)} options={KIND_OPTIONS} />
        <Button type="submit" size="sm" variant="tonal" disabled={busy || !Number(reportId)}>
          Связать
        </Button>
      </form>
      {error && (
        <div role="alert">
          <Banner tone="error">{error}</Banner>
        </div>
      )}
    </div>
  );
}

/** In a report's card: its epics (Э7, both sides) and linking to one. */
export function ReportEpics({ reportId, onError }: { reportId: number; onError: OnError }) {
  const [links, setLinks] = useState<EpicDetail['links'] | null>(null);
  const [epics, setEpics] = useState<Epic[]>([]);
  const [epicId, setEpicId] = useState('');
  const [kind, setKind] = useState<EpicLinkKind>('child');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const [mine, all] = await Promise.all([bugApi.reportEpics(reportId), bugApi.epics()]);
      setLinks(mine.links);
      setEpics(all.epics);
    } catch (err) {
      onError(err, 'Не удалось загрузить эпики репорта');
    }
  }, [reportId, onError]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!links) return null;
  const act = async (action: () => Promise<unknown>) => {
    setError('');
    try {
      await action();
      await load();
    } catch (err) {
      setError(errorText(err, 'Не удалось'));
    }
  };

  return (
    <div className="epic-links report-epics">
      <div className="board-lane-title">◆ Эпики</div>
      {links.map((l) => (
        <div key={`${l.epicId}-${l.kind}`} className="epic-link">
          <span className="mono" data-bug-open="">
            ◆ {l.epicId}
          </span>
          <span className="board-card-text" data-pii="">
            {l.epicTitle}
          </span>
          <Badge>{l.kind === 'child' ? 'входит' : 'связан'}</Badge>
          <Button size="sm" variant="quiet" aria-label={`Снять связь с эпиком №${l.epicId}`} onClick={() => void act(() => bugApi.unlinkReport(l.epicId, reportId, l.kind))}>
            ✕
          </Button>
        </div>
      ))}
      {epics.length > 0 && (
        <div className="board-actions">
          <Select
            label="Эпик"
            hideLabel
            size="sm"
            placeholder="Эпик…"
            value={epicId}
            onChange={setEpicId}
            options={epics.map((e) => ({ value: String(e.id), label: `◆ ${e.id} ${e.title}` }))}
          />
          <Select label="Связь" hideLabel size="sm" value={kind} onChange={(v) => setKind(v as EpicLinkKind)} options={KIND_OPTIONS} />
          <Button size="sm" variant="tonal" disabled={!epicId} onClick={() => void act(() => bugApi.linkReport(Number(epicId), reportId, kind))}>
            В эпик
          </Button>
        </div>
      )}
      {error && <Banner tone="error">{error}</Banner>}
    </div>
  );
}
